import { Link } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback, useState } from "react"

import { useText } from "../i18n/use-text"
import { cx } from "../lib/cx"
import { Counter } from "../ui/counter"
import styles from "./nav-tabbar.module.css"
import { Popover } from "./popover"
import type { NavEntry } from "./use-nav"
import { UserPanel } from "./user-panel"

/**
 * Нижние вкладки — раскладка телефона (360–767).
 *
 * ПОЧЕМУ НАВИГАЦИЯ ПЕРЕЕХАЛА ВНИЗ, А НЕ СЖАЛАСЬ ЕЩЁ РАЗ. Телефон и ноутбук — не одна
 * адаптивная сетка (ТЗ §7). Мастер держит телефон одной рукой и достаёт большим пальцем
 * нижнюю треть экрана; боковая полоса, даже узкая, съедает на 360 точках седьмую часть
 * ширины ради пунктов, до которых ещё надо дотянуться. Поэтому внизу — и потому же
 * высота полосы 64 px, а минимальная ширина вкладки 48: руки у мастера мокрые.
 *
 * ПОЧЕМУ НЕ ВСЕ ОДИННАДЦАТЬ РАЗДЕЛОВ. Одиннадцать вкладок на 360 точках дают по 32 пикселя
 * на пункт — мимо них промахивается кто угодно. Помещаются четыре, поэтому первые три
 * раздела остаются на виду, а остальные уходят в «Ещё» вместе с профилем и выходом.
 * Порядок при этом не перестраивается (ТЗ §4.3 п.2): на виду всегда ПЕРВЫЕ три из тех,
 * что человеку открыты, а не три самых нажимаемых. У мастера с двумя-тремя разделами
 * «Ещё» не появляется вовсе — вкладки просто занимают всю полосу.
 */
const VISIBLE_TABS = 4

export function NavTabbar(props: {
  readonly entries: readonly NavEntry[]
  readonly userName: string
  readonly roleName: string
}): ReactNode {
  const t = useText()
  const [moreOpen, setMoreOpen] = useState(false)
  const closeMore = useCallback(() => {
    setMoreOpen(false)
  }, [])

  const needsMore = props.entries.length > VISIBLE_TABS
  const visible = needsMore ? props.entries.slice(0, VISIBLE_TABS - 1) : props.entries
  const hidden = needsMore ? props.entries.slice(VISIBLE_TABS - 1) : []
  const hiddenActive = hidden.some((entry) => entry.active)
  const hiddenBadge = hidden.reduce<number | undefined>(
    (sum, entry) => (entry.badge === undefined ? sum : (sum ?? 0) + entry.badge),
    undefined,
  )

  return (
    <nav className={styles.bar} aria-label="Разделы кабинета">
      {visible.map((entry) => (
        <Link
          key={entry.section.id}
          to={entry.section.path}
          aria-current={entry.active ? "page" : undefined}
          className={cx(styles.tab, entry.active ? styles.tabActive : undefined)}
        >
          <span className={styles.mark} aria-hidden="true" />
          <span className={styles.label}>{t(entry.section.textKey)}</span>
          {entry.badge === undefined ? null : (
            <Counter value={entry.badge} label="требуют действия" tone="attention" corner />
          )}
        </Link>
      ))}

      {needsMore ? (
        <div className={styles.moreWrap}>
          <button
            type="button"
            className={cx(styles.tab, hiddenActive ? styles.tabActive : undefined)}
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            onClick={() => {
              setMoreOpen((open) => !open)
            }}
          >
            <span className={styles.mark} aria-hidden="true" />
            <span className={styles.label}>Ещё</span>
            {hiddenBadge === undefined || hiddenBadge === 0 ? null : (
              <Counter value={hiddenBadge} label="требуют действия" tone="attention" corner />
            )}
          </button>

          <Popover open={moreOpen} onClose={closeMore} label="Остальные разделы" className={styles.sheet}>
            <div className={styles.sheetList}>
              {hidden.map((entry) => (
                <Link
                  key={entry.section.id}
                  to={entry.section.path}
                  aria-current={entry.active ? "page" : undefined}
                  className={cx(styles.sheetItem, entry.active ? styles.sheetItemActive : undefined)}
                  onClick={closeMore}
                >
                  <span className={styles.sheetLabel}>{t(entry.section.textKey)}</span>
                  {entry.badge === undefined ? null : (
                    <Counter value={entry.badge} label="требуют действия" tone="attention" />
                  )}
                  {entry.dot ? (
                    <span className={styles.sheetDot}>
                      <span className="plnr-visually-hidden">требует внимания</span>
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>
            <div className={styles.sheetFooter}>
              <UserPanel userName={props.userName} roleName={props.roleName} />
            </div>
          </Popover>
        </div>
      ) : null}
    </nav>
  )
}
